import computedStyleToInlineStyle from 'computed-style-to-inline-style';
import ScratchBlocks from 'scratch-blocks';

const SVG_NS = 'http://www.w3.org/2000/svg';
const XLINK_NS = 'http://www.w3.org/1999/xlink';
const DEFAULT_PADDING = 10;
const PNG_EXPORT_SCALE = 2;

const getExportBlock = blockId => {
    const block = ScratchBlocks.getMainWorkspace().getBlockById(blockId);
    if (!block) throw new Error(`Unable to find block for export: ${blockId}`);
    return block.getRootBlock();
};

const absolutizeLinkedAssets = svg => {
    const imageElements = svg.querySelectorAll('image');
    imageElements.forEach(image => {
        ['href', 'xlink:href'].forEach(attribute => {
            const value = image.getAttribute(attribute);
            if (!value || value.startsWith('data:') || value.startsWith('blob:')) return;
            image.setAttribute(attribute, new URL(value, document.baseURI).href);
        });
    });
};

const blobToDataUrl = blob => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
});

const inlineLinkedAssets = async svg => {
    absolutizeLinkedAssets(svg);
    const imageElements = Array.from(svg.querySelectorAll('image'));

    await Promise.all(imageElements.map(async image => {
        const href = image.getAttribute('href') || image.getAttribute('xlink:href');
        if (!href || href.startsWith('data:') || href.startsWith('blob:')) return;

        try {
            const response = await fetch(href, {mode: 'cors'});
            if (!response.ok) throw new Error(`Failed to load ${href}`);
            const blob = await response.blob();
            const dataUrl = await blobToDataUrl(blob);
            image.setAttribute('href', dataUrl);
            image.setAttribute('xlink:href', dataUrl);
        } catch (error) {
            // Keep the absolute URL as a fallback if inlining fails.
            // PNG export is best-effort for externally linked assets.
            /* eslint-disable no-console */
            console.warn('Could not inline block export asset', href, error);
            /* eslint-enable no-console */
        }
    }));
};

const cloneStackIntoSvg = async blockId => {
    const exportBlock = getExportBlock(blockId);
    const exportBlocks = exportBlock.getDescendants(false);
    const rootPosition = exportBlock.getRelativeToSurfaceXY();

    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('xmlns', SVG_NS);
    svg.setAttribute('xmlns:xlink', XLINK_NS);

    const group = document.createElementNS(SVG_NS, 'g');
    svg.appendChild(group);

    exportBlocks.forEach(block => {
        const blockSvg = block.getSvgRoot();
        if (!blockSvg) return;

        const clone = blockSvg.cloneNode(true);
        clone.innerHTML = clone.innerHTML.replace(/&nbsp;/g, ' ');

        const blockPosition = block.getRelativeToSurfaceXY();
        const x = (blockPosition.x - rootPosition.x) + DEFAULT_PADDING;
        const y = (blockPosition.y - rootPosition.y) + DEFAULT_PADDING;
        clone.setAttribute('transform', `translate(${x} ${y})`);
        group.appendChild(clone);
    });

    document.body.appendChild(svg);

    computedStyleToInlineStyle(svg, {
        recursive: true,
        properties: [
            'fill',
            'stroke',
            'stroke-width',
            'font-family',
            'font-size',
            'font-weight'
        ]
    });
    await inlineLinkedAssets(svg);

    const bounds = group.getBBox();
    const viewBoxX = Math.floor(bounds.x - DEFAULT_PADDING);
    const viewBoxY = Math.floor(bounds.y - DEFAULT_PADDING);
    const width = Math.ceil(bounds.width + (DEFAULT_PADDING * 2));
    const height = Math.ceil(bounds.height + (DEFAULT_PADDING * 2));
    svg.setAttribute('width', width);
    svg.setAttribute('height', height);
    svg.setAttribute('viewBox', `${viewBoxX} ${viewBoxY} ${width} ${height}`);

    return {svg, width, height};
};

const serializeSvg = svg => (new XMLSerializer()).serializeToString(svg);

const makeSvgDataUri = svgString => `data:image/svg+xml;utf-8,${encodeURIComponent(svgString)}`;

const canvasToBlob = canvas => new Promise(resolve => {
    canvas.toBlob(blob => resolve(blob), 'image/png');
});

export const exportBlockSvgString = blockId => (
    cloneStackIntoSvg(blockId).then(({svg}) => {
        try {
            return serializeSvg(svg);
        } finally {
            svg.parentNode.removeChild(svg);
        }
    })
);

export const exportBlockSvgBlob = blockId => (
    exportBlockSvgString(blockId)
        .then(svgString => new Blob([svgString], {type: 'image/svg+xml;charset=utf-8'}))
);

export const exportBlockPngBlob = blockId => new Promise((resolve, reject) => {
    cloneStackIntoSvg(blockId)
        .then(({svg, width, height}) => {
            const svgString = serializeSvg(svg);
            svg.parentNode.removeChild(svg);

            const image = new Image();
            image.onload = async () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = width * PNG_EXPORT_SCALE;
                    canvas.height = height * PNG_EXPORT_SCALE;
                    const context = canvas.getContext('2d');
                    context.scale(PNG_EXPORT_SCALE, PNG_EXPORT_SCALE);
                    context.clearRect(0, 0, width, height);
                    context.drawImage(image, 0, 0, width, height);
                    const blob = await canvasToBlob(canvas);
                    resolve(blob);
                } catch (error) {
                    reject(error);
                }
            };
            image.onerror = reject;
            image.src = makeSvgDataUri(svgString);
        })
        .catch(reject);
});

/**
 * Given a blockId, return a data-uri image that can be used to create a thumbnail.
 * @param {string} blockId the ID of the block to imagify
 * @return {Promise} resolves to a data-url of a picture of the blocks
 */
export default function (blockId) {
    return exportBlockSvgString(blockId)
        .then(svgString => makeSvgDataUri(svgString));
}
